import { File } from "lucide-react";
import React, { useRef } from "react";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { allowFileTypes } from "@/constants/constants";
import Icons from "@/utils/Icons";

interface DriveInputsProps {
  onFileSelect: (file: File) => void;
  onFolderSelect: (files: FileList) => void;
  onAddFolder?: () => void;
  isFileInputLoading?: boolean;
  isFolderInputLoading?: boolean;
}

export const DriveInputs: React.FC<DriveInputsProps> = ({
  onFileSelect,
  onFolderSelect,
  onAddFolder,
  isFileInputLoading = false,
  isFolderInputLoading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputClick = (): void => {
    if (fileInputRef.current && !isFileInputLoading) {
      fileInputRef.current.click();
    }
  };

  const handleFolderInputClick = (): void => {
    if (folderInputRef.current && !isFolderInputLoading) {
      folderInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      onFolderSelect(e.target.files);
      // Reset the input
      if (folderInputRef.current) {
        folderInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ButtonWithIcon
            variant='outline'
            icon={
              isFileInputLoading || isFolderInputLoading ? "loading" : "add"
            }
            disabled={isFileInputLoading || isFolderInputLoading}
            className={
              isFileInputLoading || isFolderInputLoading
                ? "border-blue-400 border-2"
                : ""
            }
          >
            {isFileInputLoading || isFolderInputLoading
              ? "Uploading..."
              : "Add New"}
          </ButtonWithIcon>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={handleFileInputClick}
            disabled={isFileInputLoading}
            className={
              isFileInputLoading ? "opacity-50 cursor-not-allowed" : ""
            }
          >
            <File size={16} className='mr-2' />
            {isFileInputLoading ? "Uploading File..." : "Add New File"}
          </DropdownMenuItem>
          {onAddFolder && (
            <DropdownMenuItem onClick={onAddFolder}>
              <Icons name='folder' size={16} />
              <span className='ml-2'>Add New Folder</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handleFolderInputClick}
            disabled={isFolderInputLoading}
            className={
              isFolderInputLoading ? "opacity-50 cursor-not-allowed" : ""
            }
          >
            <Icons name='folder' size={16} />
            <span className='ml-2'>
              {isFolderInputLoading
                ? "Uploading Folder..."
                : "Upload New Folder"}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        type='file'
        accept={allowFileTypes}
        onChange={handleFileChange}
        ref={fileInputRef}
        className='hidden'
      />

      <input
        type='file'
        {...{
          webkitdirectory: "",
          mozdirectory: "",
          directory: "",
          multiple: true,
        }}
        multiple={true}
        onChange={handleFolderChange}
        ref={folderInputRef}
        className='hidden'
      />
    </>
  );
};
